import React, { useState, useEffect, useRef } from "react";
import { Sidebar } from "primereact/sidebar";
import InputComponent from "./Input";
import CheckboxComponent from "./Checkbox";
import DropdownComponent from "./Dropdown";
import RadioComponent from "./RadioButtons";
import DateComponent from "./Date";
import TextAreaComponent from "./Textarea";
import FileUploadComponent from "./FileUpload";
import InputSwitchComponent from "./InputSwitch";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import MapCoordinatesComponent from "../../GenerateForms/MapCoordinates";
import MultiSelectComponent from "./Multiselect";

const EditFieldSidebar = ({
  visible,
  onHide,
  field,
  onSave,
  existingFields,
  groups,
}) => {
  const [fieldData, setFieldData] = useState(field || {});
  const [fieldKey, setFieldKey] = useState(field?.key || "");
  const [selectedGroup, setSelectedGroup] = useState(field?.group || null);

  const toast = useRef(null);

  useEffect(() => {
    if (field) {
      setFieldData(field);
      setFieldKey(field.key);
      setSelectedGroup(field.group);
    }
  }, [field]);

  useEffect(() => {
    setFieldData((prevData) => ({ ...prevData, key: fieldKey }));
  }, [fieldKey]);

  useEffect(() => {
    setFieldData((prevData) => ({ ...prevData, group: selectedGroup }));
  }, [selectedGroup]);

  const handleSave = (updatedFieldData) => {
    const isDuplicate = existingFields.some(
      (existingField) =>
        existingField.key === fieldKey && existingField !== field
    );

    if (isDuplicate) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Field name must be unique. Please enter a different name.",
        life: 3000,
      });
      return false;
    }

    onSave({
      ...updatedFieldData,
      key: fieldKey,
      name: fieldData.name,
      group: selectedGroup,
    });
    onHide();
    return true;
  };

  const renderFieldEditor = () => {
    if (!fieldData) return null;

    switch (fieldData.type) {
      case "text":
      case "number":
      case "email":
        return (
          <InputComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            type={fieldData.type}
            required={fieldData.required}
            minValue={fieldData.minValue}
            maxValue={fieldData.maxValue}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
            maxLength={fieldData.maxLength}
          />
        );
      case "checkbox":
        return (
          <CheckboxComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
            options={fieldData.options}
          />
        );
      case "dropdown":
        return (
          <DropdownComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
            options={fieldData.options}
          />
        );
      case "multiselect":
        return (
          <MultiSelectComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
            options={fieldData.options}
          />
        );
      case "geolocation":
        return (
          <MapCoordinatesComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
          />
        );
      case "radio":
        return (
          <RadioComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
            options={fieldData.options}
          />
        );
      case "date":
        return (
          <DateComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
          />
        );
      case "textarea":
        return (
          <TextAreaComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
          />
        );
      case "inputswitch":
        return (
          <InputSwitchComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            linked={fieldData.linked}
            database={fieldData.database}
            field={fieldData.field}
          />
        );
      case "fileupload":
        return (
          <FileUploadComponent
            onSave={handleSave}
            closeSidebar={onHide}
            fieldName={fieldData.key}
            required={fieldData.required}
            fileType={fieldData.fileType}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      className="edit-field-sidebar"
      position="right"
    >
      <Toast ref={toast} />
      <h2>Edit Field</h2>
      <Divider />
      <div className="p-field mb-3">
        <label htmlFor="fieldName" style={{ fontWeight: "bold" }}>
          Field Name:
        </label>
        <InputText
          id="fieldKey"
          value={fieldKey}
          onChange={(e) => setFieldKey(e.target.value)}
          style={{ width: "100%" }}
        />
      </div>

      <div className="p-field mb-3">
        <label htmlFor="fieldGroup" style={{ fontWeight: "bold" }}>
          Field Group:
        </label>
        <Dropdown
          filter
          id="fieldGroup"
          value={selectedGroup}
          options={
            groups.length > 0
              ? groups.map((g) => ({
                  label: g.title,
                  value: g.title,
                }))
              : [{ label: "No groups available", value: null }]
          }
          onChange={(e) => setSelectedGroup(e.value)}
          placeholder="Select a group"
          className="w-full"
          showClear
        />
      </div>
      {renderFieldEditor()}
    </Sidebar>
  );
};

export default EditFieldSidebar;
